import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, setDoc, deleteDoc, Timestamp, writeBatch } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { db, functions, storage } from './firebase';
import SwimRouteEditor from './SwimRouteEditor';
import Map, { Source, Layer, Marker } from 'react-map-gl/mapbox';
import mapboxgl from 'mapbox-gl';
import TranslatableField from './TranslatableField';
import 'mapbox-gl/dist/mapbox-gl.css';
import { useMapboxToken } from './MapboxTokenContext';


const calculateBoundingBox = (route) => {
  if (!route || route.length === 0) {
    return null;
  }

  // Firestore GeoPoint has .latitude and .longitude properties
  let minLat = route[0].latitude;
  let maxLat = route[0].latitude;
  let minLng = route[0].longitude;
  let maxLng = route[0].longitude;

  for (let i = 1; i < route.length; i++) {
    const point = route[i];
    if (point.latitude < minLat) minLat = point.latitude;
    if (point.latitude > maxLat) maxLat = point.latitude;
    if (point.longitude < minLng) minLng = point.longitude;
    if (point.longitude > maxLng) maxLng = point.longitude;
  }

  return { minLat, maxLat, minLng, maxLng };
};

const resizeImage = (file, maxWidth = 1200, maxHeight = 1200) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;
      img.onload = () => {
        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > maxWidth) {
            height *= maxWidth / width;
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width *= maxHeight / height;
            height = maxHeight;
          }
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob((blob) => {
          resolve(blob);
        }, 'image/jpeg', 0.85);
      };
      img.onerror = (error) => reject(error);
    };
    reader.onerror = (error) => reject(error);
  });
};

const GreenFlag = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M5 21V4H19L17 9L19 14H5" fill="#28a745" stroke="#1b5e20" strokeWidth="2" strokeLinejoin="round"/>
  </svg>
);

const RedHexagon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M7 3H17L22 12L17 21H7L2 12L7 3Z" fill="#dc3545" stroke="#b71c1c" strokeWidth="2" strokeLinejoin="round"/>
  </svg>
);

const eventEndTimestamp = (event) => {
  const endDate = event.seriesEnd;
  if (typeof endDate?.toMillis === 'function') return endDate.toMillis();
  if (typeof endDate?.toDate === 'function') return endDate.toDate().getTime();
  if (endDate instanceof Date) return endDate.getTime();
  if (typeof endDate === 'string' || typeof endDate === 'number') {
    const timestamp = new Date(endDate).getTime();
    return Number.isNaN(timestamp) ? 0 : timestamp;
  }
  return 0;
};

const OfficialEventsPage = () => {
  const mapboxToken = useMapboxToken();
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [swims, setSwims] = useState([]);
  const [selectedSwim, setSelectedSwim] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [isEditingEvent, setIsEditingEvent] = useState(false);
  const [eventFormData, setEventFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [isCreatingSwim, setIsCreatingSwim] = useState(false);
  const [isCreatingEvent, setIsCreatingEvent] = useState(false);
  const [isEditingRoute, setIsEditingRoute] = useState(false);
  const [swimMatches, setSwimMatches] = useState({});
  const [isUploading, setIsUploading] = useState(false);
  const [matchingLoading, setMatchingLoading] = useState({});
  const [updatingLoading, setUpdatingLoading] = useState({});
  const [isFullScreenMap, setIsFullScreenMap] = useState(false);
  const [mapViewStyle, setMapViewStyle] = useState('mapbox://styles/mapbox/satellite-v9');
  const [prelimMatchData, setprelimMatchData] = useState(null); // { matches: [], routes: {id: [[lng,lat]...]} }
  const [loadingPrelimMatches, setLoadingPrelimMatches] = useState(false);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const querySnapshot = await getDocs(collection(db, 'OfficialEvents'));
      const eventsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })).sort((first, second) => eventEndTimestamp(second) - eventEndTimestamp(first));
      setEvents(eventsData);
    } catch (error) {
      console.error("Error fetching events:", error);
    }
    setLoading(false);
  };

  // Fetch Events List
  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchSwimsForEvent = async (eventId) => {
    setLoading(true);
    try {
      const swimsRef = collection(db, 'OfficialEvents', eventId, 'OfficialEventSwims');
      const querySnapshot = await getDocs(swimsRef);
      const swimsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setSwims(swimsData);
    } catch (error) {
      console.error("Error fetching swims:", error);
    }
    setLoading(false);
  };

  // Fetch Swims for Selected Event
  useEffect(() => {
    if (selectedEvent) {
      fetchSwimsForEvent(selectedEvent.id);
      setIsEditingEvent(false); // Reset edit mode when switching events
    }
  }, [selectedEvent]);

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    if (timestamp.toDate) return timestamp.toDate().toLocaleDateString();
    return new Date(timestamp).toLocaleDateString();
  };
  
  const formatDateTime = (timestamp) => {
      if (!timestamp) return 'N/A';
      if (timestamp.toDate) return timestamp.toDate().toLocaleString();
      return new Date(timestamp).toLocaleString();
  }

  const getDatetimeString = (timestamp) => {
    if (!timestamp) return '';
    try {
        // Handle Firestore Timestamp or JS Date
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        // Format for datetime-local (YYYY-MM-DDTHH:mm)
        const offset = date.getTimezoneOffset() * 60000;
        return new Date(date.getTime() - offset).toISOString().slice(0, 16);
    } catch (e) {
        return '';
    }
  };

  const handleSwimClick = (swim) => {
    setSelectedSwim(swim);
    setIsCreatingSwim(false);
    setprelimMatchData(null);
    
    const startString = getDatetimeString(swim.officialStart);

    setEditFormData({
        ...swim,
        officialStart: startString,
        // Ensure seriesId is set to current event if missing
        seriesId: swim.seriesId || selectedEvent.id,
        route: swim.route || []
    });
  };

  const handleAddSwimClick = () => {
    setIsCreatingSwim(true);
    setSelectedSwim({ id: 'new' }); // Placeholder to trigger the form view
    setprelimMatchData(null);
    
    setEditFormData({
        id: '', // User must enter a new ID
        seriesId: selectedEvent.id,
        swimName: '',
        assetKey: selectedEvent.assetKey || '',
        readMoreHyperlink: selectedEvent.readMoreHyperlink || '',
        signUpHyperlink: selectedEvent.signUpHyperlink || '',
        route: [] // Initialize route as empty array
    });
  };

  const handleRouteSave = (newRoute) => {
    setEditFormData(prev => ({
      ...prev,
      route: newRoute
    }));
    setIsEditingRoute(false);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    
    setEditFormData(prev => {
        const updated = {
            ...prev,
            [name]: newValue
        };
        
        // Clear dependent fields when prerequisites are removed
        if (name === 'signUpHyperlink' && (!newValue || newValue.trim() === '')) {
            updated.discountedSignUpHyperlink = '';
            updated.discountCode = '';
        } else if (name === 'discountedSignUpHyperlink' && (!newValue || newValue.trim() === '')) {
            updated.discountCode = '';
        }
        
        return updated;
    });
  };

  const handleCheckMatches = async (e, swim) => {
    e.stopPropagation();
    setMatchingLoading(prev => ({ ...prev, [swim.id]: true }));
    try {
        const matchOfficialSwim = httpsCallable(functions, 'matchOfficialSwim');
        const result = await matchOfficialSwim({ 
            seriesId: selectedEvent.id, 
            swimId: swim.id 
        });
        setSwimMatches(prev => ({ ...prev, [swim.id]: result.data.matches }));
    } catch (error) {
        console.error("Error matching:", error);
        alert("Error matching: " + error.message);
    }
    setMatchingLoading(prev => ({ ...prev, [swim.id]: false }));
  };

  const handleLoadPrelimMatches = async () => {
    if (!selectedSwim || !selectedEvent) return;
    setLoadingPrelimMatches(true);
    setprelimMatchData(null);
    try {
      const matchOfficialSwimFn = httpsCallable(functions, 'matchOfficialSwim');
      const result = await matchOfficialSwimFn({
        seriesId: selectedEvent.id,
        swimId: selectedSwim.id
      });
      const matches = result.data.matches;

      // Build routes map from the routePoints returned by the cloud function
      // (avoids a separate client-side Firestore read that would be blocked by security rules)
      const routes = {};
      matches.forEach(m => {
        if (m.routePoints && m.routePoints.length >= 2) {
          routes[m.id] = m.routePoints.map(p => [p.lng, p.lat]);
        }
      });

      setprelimMatchData({ matches, routes });
    } catch (error) {
      console.error('Error loading preliminary matches:', error);
      alert('Error loading matches: ' + error.message);
    }
    setLoadingPrelimMatches(false);
  };

  const handleUpdateMatch = async (seriesId, officialSwimId, userId, userSwimId) => {
    const key = `${officialSwimId}-${userSwimId}`;
    setUpdatingLoading(prev => ({ ...prev, [key]: true }));
    try {
      const updateMatchedSwim = httpsCallable(functions, 'updateMatchedSwim');
      const response = await updateMatchedSwim({
        seriesId,
        officialSwimId,
        userId,
        userSwimId
      });

      if (response.data.success) {
        console.log("Match updated successfully:", response.data.matchData);
        alert("Successfully updated the user's swim with the official match!");
        // Optionally, update UI to show a "completed" state
      } else {
        console.error("Update failed:", response.data.message);
        alert(`Update failed: ${response.data.message}`);
      }
    } catch (error) {
      console.error("Error calling updateMatchedSwim:", error);
      alert("An error occurred while updating the match: " + error.message);
    }
    setUpdatingLoading(prev => ({ ...prev, [key]: false }));
  };

  const handleSaveSwim = async (e) => {
    e.preventDefault();
    if (!selectedEvent || !editFormData) return;
    setLoading(true);

    try {
        const oldId = isCreatingSwim ? null : selectedSwim.id;
        const newId = editFormData.id;
        const seriesId = selectedEvent.id;

        // Construct data object
        const dataToSave = { ...editFormData };
        
        // Fix Date
        if (dataToSave.officialStart) {
            dataToSave.officialStart = Timestamp.fromDate(new Date(dataToSave.officialStart));
        }
        
        // Fix Numbers
        if (dataToSave.distanceInMeters) {
            dataToSave.distanceInMeters = Number(dataToSave.distanceInMeters);
        }

        // Sanitize optional fields
        if (!dataToSave.signUpHyperlink) dataToSave.signUpHyperlink = null;
        if (!dataToSave.discountedSignUpHyperlink) dataToSave.discountedSignUpHyperlink = null;
        if (!dataToSave.readMoreHyperlink) dataToSave.readMoreHyperlink = null;
        if (!dataToSave.discountCode) dataToSave.discountCode = null;

        // Ensure seriesId is locked/correct
        dataToSave.seriesId = seriesId;
        
        // Ensure route exists
        if (!dataToSave.route) dataToSave.route = [];

        // Calculate and add geoBoundingBox from the route
        if (dataToSave.route && dataToSave.route.length > 0) {
            dataToSave.geoBoundingBox = calculateBoundingBox(dataToSave.route);
        } else {
            // If there's no route, ensure there's no bounding box.
            // Using `delete` is safer than setting to null if the field might not exist.
            delete dataToSave.geoBoundingBox;
        }

        // Handle ID change (Rename Document)
        if (oldId && oldId !== newId) {
            // Create new doc
            await setDoc(doc(db, 'OfficialEvents', seriesId, 'OfficialEventSwims', newId), dataToSave);
            // Delete old doc
            await deleteDoc(doc(db, 'OfficialEvents', seriesId, 'OfficialEventSwims', oldId));
        } else {
            // Update existing
            await setDoc(doc(db, 'OfficialEvents', seriesId, 'OfficialEventSwims', newId), dataToSave);
        }

        // Refresh list
        await fetchSwimsForEvent(seriesId);
        setSelectedSwim(null); // Close detail view
        setIsCreatingSwim(false);

    } catch (error) {
        console.error("Error saving swim:", error);
        alert("Failed to save swim: " + error.message);
    }
    setLoading(false);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !selectedEvent) return;

    setIsUploading(true);

    // Use a consistent name for the image to make it easy to manage.
    const imagePath = `events/${selectedEvent.id}/marketingImage.jpg`;
    const newImageRef = ref(storage, imagePath);

    try {
        const resizedImage = await resizeImage(file);
        // Upload the new file. This will overwrite if the file already exists at the same path.
        await uploadBytes(newImageRef, resizedImage);

        // Get the download URL and update the firestore document with it.
        const downloadUrl = await getDownloadURL(newImageRef);
        const eventRef = doc(db, 'OfficialEvents', selectedEvent.id);
        await setDoc(eventRef, { marketingImageUrl: downloadUrl }, { merge: true });

        // Refresh the UI
        alert("Image uploaded successfully!");
        const updatedEvent = { ...selectedEvent, marketingImageUrl: downloadUrl };
        setSelectedEvent(updatedEvent);
        setEvents(prevEvents => prevEvents.map(evt => evt.id === selectedEvent.id ? updatedEvent : evt));

    } catch (error) {
        console.error("Error uploading image:", error);
        alert("Image upload failed: " + error.message);
    } finally {
        setIsUploading(false);
    }
  };

  if (isEditingRoute && selectedSwim) {
    return (
      <div className="official-events-detail">
        <h2>Edit Route: {typeof editFormData.swimName === 'string' ? editFormData.swimName : (editFormData.swimName?.en || 'New Swim')}</h2>
        <SwimRouteEditor
          initialRoute={editFormData.route}
          onSave={handleRouteSave}
          onCancel={() => setIsEditingRoute(false)}
          seriesId={selectedEvent?.id || null}
          swimId={(!isCreatingSwim && selectedSwim && selectedSwim.id !== 'new') ? selectedSwim.id : null}
        />
      </div>
    );
  }

  const cycleMapStyle = () => {
    setMapViewStyle(prev => {
      if (prev === 'mapbox://styles/mapbox/satellite-v9') return 'mapbox://styles/mapbox/satellite-streets-v12';
      if (prev === 'mapbox://styles/mapbox/satellite-streets-v12') return 'mapbox://styles/mapbox/streets-v11';
      return 'mapbox://styles/mapbox/satellite-v9';
    });
  };

  const getStyleButtonLabel = () => {
    if (mapViewStyle === 'mapbox://styles/mapbox/satellite-v9') return '🛰️ Satellite';
    if (mapViewStyle === 'mapbox://styles/mapbox/satellite-streets-v12') return '🏘️ Hybrid';
    return '🗺️ Map';
  };

  if (selectedSwim) {
    return (
      <div className="official-events-detail">
        <button className="back-button" onClick={() => setSelectedSwim(null)} style={{marginBottom: '20px', padding: '8px 16px', cursor: 'pointer'}}>
          &larr; Back to Swims
        </button>
        <h2>{isCreatingSwim ? 'Add New Swim' : `Edit Swim: ${typeof selectedSwim.swimName === 'string' ? selectedSwim.swimName : (selectedSwim.swimName?.en || 'Untitled')}`}</h2>

        {isFullScreenMap && editFormData.route && editFormData.route.length > 0 && (
            <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100vw',
                height: '100vh',
                zIndex: 2000,
                backgroundColor: 'white',
                display: 'flex',
                flexDirection: 'column'
            }}>
                <div style={{ padding: '10px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #ccc', backgroundColor: '#f8f9fa', flexWrap: 'wrap', gap: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                        <h3 style={{ margin: 0 }}>Route Preview: {typeof editFormData.swimName === 'string' ? editFormData.swimName : (editFormData.swimName?.en || 'New Swim')}</h3>
                        {prelimMatchData && (
                            <div style={{ display: 'flex', gap: '12px', alignItems: 'center', fontSize: '0.8rem', color: '#444' }}>
                                <strong>{prelimMatchData.matches.length} overlays:</strong>
                                <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                    <span style={{ display: 'inline-block', width: '18px', height: '4px', backgroundColor: '#22c55e', borderRadius: '2px' }}></span> Match
                                </span>
                                <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                    <span style={{ display: 'inline-block', width: '18px', height: '4px', backgroundColor: '#f97316', borderRadius: '2px' }}></span> No match
                                </span>
                            </div>
                        )}
                    </div>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <button type="button" onClick={cycleMapStyle} style={{ padding: '5px 15px', fontSize: '0.9rem' }}>
                            {getStyleButtonLabel()}
                        </button>
                        <button type="button" onClick={() => setIsFullScreenMap(false)} style={{ backgroundColor: '#dc3545', padding: '5px 15px', fontSize: '0.9rem' }}>
                            Close
                        </button>
                    </div>
                </div>
                <div style={{ flex: 1 }}>
                    <Map
                        initialViewState={{
                            bounds: (() => {
                                const bounds = new mapboxgl.LngLatBounds();
                                editFormData.route.forEach(p => bounds.extend([p.longitude, p.latitude]));
                                return bounds;
                            })(),
                            fitBoundsOptions: { padding: 50 }
                        }}
                        style={{ width: '100%', height: '100%' }}
                        mapStyle={mapViewStyle}
                        mapboxAccessToken={mapboxToken}
                    >
                        <Source id="full-map-route" type="geojson" data={{
                            type: 'Feature',
                            geometry: {
                                type: 'LineString',
                                coordinates: editFormData.route.map(p => [p.longitude, p.latitude])
                            }
                        }}>
                            <Layer
                                id="full-map-line"
                                type="line"
                                layout={{ 'line-join': 'round', 'line-cap': 'round' }}
                                paint={{ 'line-color': '#007bff', 'line-width': 4 }}
                            />
                        </Source>
                        {prelimMatchData && prelimMatchData.matches.map((m) => {
                            const coords = prelimMatchData.routes[m.id];
                            if (!coords || coords.length < 2) return null;
                            return (
                                <Source key={`full-prelim-${m.id}`} id={`full-prelim-${m.id}`} type="geojson" data={{ type: 'Feature', geometry: { type: 'LineString', coordinates: coords } }}>
                                    <Layer
                                        id={`full-prelim-line-${m.id}`}
                                        type="line"
                                        layout={{ 'line-join': 'round', 'line-cap': 'round' }}
                                        paint={{ 'line-color': m.routeMatch ? '#22c55e' : '#f97316', 'line-width': 2.5, 'line-opacity': 0.85 }}
                                    />
                                </Source>
                            );
                        })}
                        <Marker longitude={editFormData.route[0].longitude} latitude={editFormData.route[0].latitude} anchor="bottom">
                            <GreenFlag />
                        </Marker>
                        <Marker longitude={editFormData.route[editFormData.route.length - 1].longitude} latitude={editFormData.route[editFormData.route.length - 1].latitude} anchor="center">
                            <RedHexagon />
                        </Marker>
                    </Map>
                </div>
            </div>
        )}

        <form onSubmit={handleSaveSwim} style={{display: 'flex', flexDirection: 'column', gap: '15px', maxWidth: '600px'}}>
            
            <div className="form-group">
                <label style={{display: 'block', marginBottom: '5px', fontWeight: 'bold'}}>ID (Document ID):</label>
                <input name="id" value={editFormData.id || ''} onChange={handleInputChange} required style={{width: '100%', padding: '8px'}} />
                {!isCreatingSwim && <small style={{color: '#666'}}>Changing this will rename the document.</small>}
            </div>

            <div className="form-group">
                <label style={{display: 'block', marginBottom: '5px', fontWeight: 'bold'}}>Series ID (Locked):</label>
                <input name="seriesId" value={editFormData.seriesId || ''} readOnly style={{width: '100%', padding: '8px', backgroundColor: '#eee'}} />
            </div>

            <TranslatableField 
                label="Swim Name" 
                name="swimName" 
                value={editFormData.swimName} 
                onChange={handleInputChange} 
            />

            <div className="form-group">
                <label style={{display: 'block', marginBottom: '5px', fontWeight: 'bold'}}>Official Start:</label>
                <input type="datetime-local" name="officialStart" value={editFormData.officialStart || ''} onChange={handleInputChange} required style={{width: '100%', padding: '8px'}} />
            </div>

            <div className="form-group">
                <label style={{display: 'block', marginBottom: '5px', fontWeight: 'bold'}}>Distance (Meters):</label>
                <input type="number" name="distanceInMeters" value={editFormData.distanceInMeters || ''} onChange={handleInputChange} style={{width: '100%', padding: '8px'}} />
            </div>

            <TranslatableField 
                label="Description" 
                name="swimDescription" 
                value={editFormData.swimDescription} 
                onChange={handleInputChange} 
                isTextarea={true} 
            />

            <div className="form-group">
                <label style={{display: 'block', marginBottom: '5px', fontWeight: 'bold'}}>Asset Key:</label>
                <input name="assetKey" value={editFormData.assetKey || ''} onChange={handleInputChange} style={{width: '100%', padding: '8px'}} />
            </div>

            <div className="form-group">
                <label style={{display: 'block', marginBottom: '5px', fontWeight: 'bold'}}>Read More URL:</label>
                <input name="readMoreHyperlink" value={editFormData.readMoreHyperlink || ''} onChange={handleInputChange} style={{width: '100%', padding: '8px'}} />
            </div>

            <div className="form-group">
                <label style={{display: 'block', marginBottom: '5px', fontWeight: 'bold'}}>Sign Up URL:</label>
                <input name="signUpHyperlink" value={editFormData.signUpHyperlink || ''} onChange={handleInputChange} style={{width: '100%', padding: '8px'}} />
            </div>
                    <div className="form-group">
                        <label style={{display: 'block', marginBottom: '5px', fontWeight: 'bold'}}>Discounted Sign Up URL:</label>
                        <input 
                            name="discountedSignUpHyperlink" 
                            value={editFormData.discountedSignUpHyperlink || ''} 
                            onChange={handleInputChange} 
                            disabled={!editFormData.signUpHyperlink || editFormData.signUpHyperlink.trim() === ''} 
                            style={{
                                width: '100%', 
                                padding: '8px',
                                backgroundColor: (!editFormData.signUpHyperlink || editFormData.signUpHyperlink.trim() === '') ? '#f5f5f5' : 'white'
                            }} 
                        />
                        {(!editFormData.signUpHyperlink || editFormData.signUpHyperlink.trim() === '') && (
                            <small style={{color: '#666', fontSize: '0.8em'}}>Requires Sign Up URL to be filled first</small>
                        )}
                    </div>
                    <div className="form-group">
                        <label style={{display: 'block', marginBottom: '5px', fontWeight: 'bold'}}>Discount Code:</label>
                        <input 
                            name="discountCode" 
                            value={editFormData.discountCode || ''} 
                            onChange={handleInputChange} 
                            disabled={!editFormData.discountedSignUpHyperlink || editFormData.discountedSignUpHyperlink.trim() === ''} 
                            style={{
                                width: '100%', 
                                padding: '8px',
                                backgroundColor: (!editFormData.discountedSignUpHyperlink || editFormData.discountedSignUpHyperlink.trim() === '') ? '#f5f5f5' : 'white'
                            }} 
                        />
                        {(!editFormData.discountedSignUpHyperlink || editFormData.discountedSignUpHyperlink.trim() === '') && (
                            <small style={{color: '#666', fontSize: '0.8em'}}>Requires Discounted Sign Up URL to be filled first</small>
                        )}
                    </div>

            <div className="form-group">
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '5px' }}>
                    <label style={{ fontWeight: 'bold' }}>Route:</label>
                    {editFormData.route && editFormData.route.length > 0 && (
                        <button 
                            type="button" 
                            onClick={() => setIsFullScreenMap(true)}
                            title="View Fullscreen"
                            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.2rem', padding: 0, display: 'flex', alignItems: 'center', color: 'black' }}
                        >
                            ⛶
                        </button>
                    )}
                </div>
                {editFormData.route && editFormData.route.length > 0 && (
                    <div style={{height: '200px', width: '100%', marginBottom: '10px', borderRadius: '4px', overflow: 'hidden', border: '1px solid #ccc'}}>
                        <Map
                            initialViewState={{
                                bounds: (() => {
                                    const bounds = new mapboxgl.LngLatBounds();
                                    editFormData.route.forEach(p => bounds.extend([p.longitude, p.latitude]));
                                    return bounds;
                                })(),
                                fitBoundsOptions: { padding: 20, maxZoom: 15 }
                            }}
                            style={{width: '100%', height: '100%'}}
                            mapStyle={mapViewStyle}
                            mapboxAccessToken={mapboxToken}
                        >
                            <Source id="mini-map-route" type="geojson" data={{
                                type: 'Feature',
                                geometry: {
                                    type: 'LineString',
                                    coordinates: editFormData.route.map(p => [p.longitude, p.latitude])
                                }
                            }}>
                                <Layer
                                    id="mini-map-line"
                                    type="line"
                                    layout={{ 'line-join': 'round', 'line-cap': 'round' }}
                                    paint={{ 'line-color': '#007bff', 'line-width': 3 }}
                                />
                            </Source>
                            {prelimMatchData && prelimMatchData.matches.map((m) => {
                                const coords = prelimMatchData.routes[m.id];
                                if (!coords || coords.length < 2) return null;
                                return (
                                    <Source key={`mini-prelim-${m.id}`} id={`mini-prelim-${m.id}`} type="geojson" data={{ type: 'Feature', geometry: { type: 'LineString', coordinates: coords } }}>
                                        <Layer
                                            id={`mini-prelim-line-${m.id}`}
                                            type="line"
                                            layout={{ 'line-join': 'round', 'line-cap': 'round' }}
                                            paint={{ 'line-color': m.routeMatch ? '#22c55e' : '#f97316', 'line-width': 2, 'line-opacity': 0.85 }}
                                        />
                                    </Source>
                                );
                            })}
                            <Marker longitude={editFormData.route[0].longitude} latitude={editFormData.route[0].latitude} anchor="bottom">
                                <GreenFlag />
                            </Marker>
                            <Marker longitude={editFormData.route[editFormData.route.length - 1].longitude} latitude={editFormData.route[editFormData.route.length - 1].latitude} anchor="center">
                                <RedHexagon />
                            </Marker>
                        </Map>
                    </div>
                )}
                <div style={{display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap'}}>
                    <span>{editFormData.route ? `${editFormData.route.length} Waypoints` : 'No Route Data'}</span>
                    <button type="button" onClick={() => setIsEditingRoute(true)} style={{padding: '5px 10px', backgroundColor: '#17a2b8', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer'}}>Edit Route Map</button>
                    {!isCreatingSwim && (
                        <button
                            type="button"
                            onClick={handleLoadPrelimMatches}
                            disabled={loadingPrelimMatches}
                            style={{padding: '5px 10px', backgroundColor: '#6f42c1', color: 'white', border: 'none', borderRadius: '4px', cursor: loadingPrelimMatches ? 'default' : 'pointer', opacity: loadingPrelimMatches ? 0.7 : 1}}
                        >
                            {loadingPrelimMatches ? '⏳ Loading...' : '🔍 Load Preliminary Matches'}
                        </button>
                    )}
                </div>
                {prelimMatchData && (
                    <div style={{marginTop: '8px', display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap', fontSize: '0.82rem', color: '#555'}}>
                        <strong>Overlays ({prelimMatchData.matches.length}):</strong>
                        <span style={{display: 'flex', alignItems: 'center', gap: '5px'}}>
                            <span style={{display: 'inline-block', width: '20px', height: '4px', backgroundColor: '#22c55e', borderRadius: '2px'}}></span> Route match
                        </span>
                        <span style={{display: 'flex', alignItems: 'center', gap: '5px'}}>
                            <span style={{display: 'inline-block', width: '20px', height: '4px', backgroundColor: '#f97316', borderRadius: '2px'}}></span> No match
                        </span>
                        <button type="button" onClick={() => setprelimMatchData(null)} style={{padding: '1px 7px', fontSize: '0.75rem', backgroundColor: '#888', color: 'white', border: 'none', borderRadius: '3px', cursor: 'pointer'}}>✕ Clear</button>
                    </div>
                )}
            </div>

            <button type="submit" disabled={loading} style={{marginTop: '20px', padding: '10px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer'}}>
                {loading ? 'Saving...' : 'Save Changes'}
            </button>
        </form>
      </div>
    );
  }

  const handleCreateEventClick = () => {
    setIsCreatingEvent(true);
    setEventFormData({
        id: '',
        seriesName: '',
        seriesLocation: '',
        seriesDescription: '',
        assetKey: '',
        readMoreHyperlink: '',
        signUpHyperlink: '',
    });
  };

  const handleEditEventClick = () => {
    setEventFormData({
        ...selectedEvent,
        seriesStart: getDatetimeString(selectedEvent.seriesStart),
        seriesEnd: getDatetimeString(selectedEvent.seriesEnd)
    });
    setIsEditingEvent(true);
  };

  const handleEventInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    
    setEventFormData(prev => {
        const updated = {
            ...prev,
            [name]: newValue
        };
        
        // Clear dependent fields when prerequisites are removed
        if (name === 'signUpHyperlink' && (!newValue || newValue.trim() === '')) {
            updated.discountedSignUpHyperlink = '';
            updated.discountCode = '';
        } else if (name === 'discountedSignUpHyperlink' && (!newValue || newValue.trim() === '')) {
            updated.discountCode = '';
        }
        
        return updated;
    });
  };

  const handleSaveEvent = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
        const oldId = isCreatingEvent ? null : selectedEvent.id;
        const newId = eventFormData.id;
        const dataToSave = { ...eventFormData };

        // Convert dates
        if (dataToSave.seriesStart) dataToSave.seriesStart = Timestamp.fromDate(new Date(dataToSave.seriesStart));
        if (dataToSave.seriesEnd) dataToSave.seriesEnd = Timestamp.fromDate(new Date(dataToSave.seriesEnd));

        // Sanitize optional fields
        if (!dataToSave.signUpHyperlink) dataToSave.signUpHyperlink = null;
        if (!dataToSave.discountedSignUpHyperlink) dataToSave.discountedSignUpHyperlink = null;
        if (!dataToSave.readMoreHyperlink) dataToSave.readMoreHyperlink = null;
        if (!dataToSave.discountCode) dataToSave.discountCode = null;

        if (oldId && oldId !== newId) {
            // --- ID CHANGED: MIGRATION REQUIRED ---
            
            // 1. Create new Series Document
            await setDoc(doc(db, 'OfficialEvents', newId), dataToSave);

            // 2. Move Swims (Fetch, Batch Write, Delete Old)
            const oldSwimsRef = collection(db, 'OfficialEvents', oldId, 'OfficialEventSwims');
            const oldSwimsSnap = await getDocs(oldSwimsRef);
            
            if (!oldSwimsSnap.empty) {
                const batch = writeBatch(db);
                oldSwimsSnap.forEach(docSnap => {
                    const swimData = docSnap.data();
                    // Update the denormalized seriesId inside the swim
                    swimData.seriesId = newId; 
                    
                    const newSwimRef = doc(db, 'OfficialEvents', newId, 'OfficialEventSwims', docSnap.id);
                    batch.set(newSwimRef, swimData);
                    batch.delete(docSnap.ref);
                });
                await batch.commit();
            }

            // 3. Delete old Series Document
            await deleteDoc(doc(db, 'OfficialEvents', oldId));
            
            // Update local state
            setSelectedEvent({ id: newId, ...dataToSave });
            // Refresh swims for the new ID
            await fetchSwimsForEvent(newId);

        } else {
            // --- ID UNCHANGED: SIMPLE UPDATE ---
            await setDoc(doc(db, 'OfficialEvents', newId), dataToSave);
            setSelectedEvent({ id: newId, ...dataToSave });
        }

        setIsEditingEvent(false);
        setIsCreatingEvent(false);
        fetchEvents(); // Refresh main list

    } catch (error) {
        console.error("Error saving event:", error);
        alert("Failed to save event: " + error.message);
    }
    setLoading(false);
  };

  if (selectedEvent || isCreatingEvent) {
    return (
      <div className="official-events-detail">
        <button className="back-button" onClick={() => { setSelectedEvent(null); setIsCreatingEvent(false); }} style={{marginBottom: '20px', padding: '8px 16px', cursor: 'pointer'}}>
          &larr; Back to Events
        </button>

        {isEditingEvent || isCreatingEvent ? (
            <div className="edit-event-form" style={{marginBottom: '30px', padding: '20px', border: '1px solid #ccc', borderRadius: '8px'}}>
                <h3>{isCreatingEvent ? 'Create New Event Series' : 'Edit Event Series'}</h3>
                <form onSubmit={handleSaveEvent} style={{display: 'flex', flexDirection: 'column', gap: '15px'}}>
                    <div className="form-group">
                        <label style={{display: 'block', marginBottom: '5px', fontWeight: 'bold'}}>ID (Document ID):</label>
                        <input name="id" value={eventFormData.id || ''} onChange={handleEventInputChange} required style={{width: '100%', padding: '8px'}} />
                        <small style={{color: '#d9534f'}}>Warning: Changing this will move all swims to the new ID.</small>
                    </div>
                    <TranslatableField 
                        label="Series Name" 
                        name="seriesName" 
                        value={eventFormData.seriesName} 
                        onChange={handleEventInputChange} 
                    />
                    <div className="form-group">
                        <label style={{display: 'block', marginBottom: '5px', fontWeight: 'bold'}}>Location:</label>
                        <input name="seriesLocation" value={eventFormData.seriesLocation || ''} onChange={handleEventInputChange} style={{width: '100%', padding: '8px'}} />
                    </div>
                    <TranslatableField 
                        label="Description" 
                        name="seriesDescription" 
                        value={eventFormData.seriesDescription} 
                        onChange={handleEventInputChange} 
                        isTextarea={true} 
                    />
                    <div style={{display: 'flex', gap: '20px'}}>
                        <div className="form-group" style={{flex: 1}}>
                            <label style={{display: 'block', marginBottom: '5px', fontWeight: 'bold'}}>Start Date:</label>
                            <input type="datetime-local" name="seriesStart" value={eventFormData.seriesStart || ''} onChange={handleEventInputChange} style={{width: '100%', padding: '8px'}} />
                        </div>
                        <div className="form-group" style={{flex: 1}}>
                            <label style={{display: 'block', marginBottom: '5px', fontWeight: 'bold'}}>End Date:</label>
                            <input type="datetime-local" name="seriesEnd" value={eventFormData.seriesEnd || ''} onChange={handleEventInputChange} style={{width: '100%', padding: '8px'}} />
                        </div>
                    </div>
                    <div className="form-group">
                        <label style={{display: 'block', marginBottom: '5px', fontWeight: 'bold'}}>Marketing Image URL Path:</label>
                        <input name="marketingImageUrl" value={eventFormData.marketingImageUrl || ''} readOnly style={{width: '100%', padding: '8px', backgroundColor: '#eee'}} />
                    </div>
                    <div className="form-group">
                        <label style={{display: 'block', marginBottom: '5px', fontWeight: 'bold'}}>Asset Key:</label>
                        <input name="assetKey" value={eventFormData.assetKey || ''} onChange={handleEventInputChange} style={{width: '100%', padding: '8px'}} />
                    </div>
                    <div className="form-group">
                        <label style={{display: 'block', marginBottom: '5px', fontWeight: 'bold'}}>Read More URL:</label>
                        <input name="readMoreHyperlink" value={eventFormData.readMoreHyperlink || ''} onChange={handleEventInputChange} style={{width: '100%', padding: '8px'}} />
                    </div>
                    <div className="form-group">
                        <label style={{display: 'block', marginBottom: '5px', fontWeight: 'bold'}}>Sign Up URL:</label>
                        <input name="signUpHyperlink" value={eventFormData.signUpHyperlink || ''} onChange={handleEventInputChange} style={{width: '100%', padding: '8px'}} />
                    </div>
                    <div className="form-group">
                        <label style={{display: 'block', marginBottom: '5px', fontWeight: 'bold'}}>Discounted Sign Up URL:</label>
                        <input 
                            name="discountedSignUpHyperlink" 
                            value={eventFormData.discountedSignUpHyperlink || ''} 
                            onChange={handleEventInputChange} 
                            disabled={!eventFormData.signUpHyperlink || eventFormData.signUpHyperlink.trim() === ''} 
                            style={{
                                width: '100%', 
                                padding: '8px',
                                backgroundColor: (!eventFormData.signUpHyperlink || eventFormData.signUpHyperlink.trim() === '') ? '#f5f5f5' : 'white'
                            }} 
                        />
                        {(!eventFormData.signUpHyperlink || eventFormData.signUpHyperlink.trim() === '') && (
                            <small style={{color: '#666', fontSize: '0.8em'}}>Requires Sign Up URL to be filled first</small>
                        )}
                    </div>
                    <div className="form-group">
                        <label style={{display: 'block', marginBottom: '5px', fontWeight: 'bold'}}>Discount Code:</label>
                        <input 
                            name="discountCode" 
                            value={eventFormData.discountCode || ''} 
                            onChange={handleEventInputChange} 
                            disabled={!eventFormData.discountedSignUpHyperlink || eventFormData.discountedSignUpHyperlink.trim() === ''} 
                            style={{
                                width: '100%', 
                                padding: '8px',
                                backgroundColor: (!eventFormData.discountedSignUpHyperlink || eventFormData.discountedSignUpHyperlink.trim() === '') ? '#f5f5f5' : 'white'
                            }} 
                        />
                        {(!eventFormData.discountedSignUpHyperlink || eventFormData.discountedSignUpHyperlink.trim() === '') && (
                            <small style={{color: '#666', fontSize: '0.8em'}}>Requires Discounted Sign Up URL to be filled first</small>
                        )}
                    </div>
                    <div style={{display: 'flex', gap: '10px'}}>
                        <button type="submit" style={{padding: '10px 20px', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer'}}>Save Series</button>
                        <button type="button" onClick={() => { setIsEditingEvent(false); setIsCreatingEvent(false); }} style={{padding: '10px 20px', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer'}}>Cancel</button>
                    </div>
                </form>
            </div>
        ) : (
        <>
        <h2>{typeof selectedEvent.seriesName === 'string' ? selectedEvent.seriesName : (selectedEvent.seriesName?.en || 'Untitled')}</h2>
        <p style={{ marginTop: '-10px', marginBottom: '15px', color: '#888', fontSize: '0.8rem' }}>ID: {selectedEvent.id}</p>
        <div className="event-image-container" style={{ marginBottom: '20px' }}>
            {selectedEvent.marketingImageUrl ? (
                <img src={selectedEvent.marketingImageUrl} alt={selectedEvent.seriesName} style={{ maxWidth: '100%', maxHeight: '300px', borderRadius: '8px', border: '1px solid #ddd' }} />
            ) : (
                <div style={{ width: '100%', height: '200px', backgroundColor: '#f0f0f0', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#888' }}>No Image</div>
            )}
            <div style={{ marginTop: '10px' }}>
                <input
                    type="file"
                    id="imageUpload"
                    style={{ display: 'none' }}
                    onChange={handleImageUpload}
                    accept="image/png, image/jpeg"
                />
                <button type="button" onClick={() => document.getElementById('imageUpload').click()} disabled={isUploading} style={{padding: '8px 12px', cursor: 'pointer'}}>
                    {isUploading ? 'Uploading...' : 'Upload New Image'}
                </button>
            </div>
        </div>

        <p><strong>Location:</strong> {selectedEvent.seriesLocation}</p>
        <p><strong>Dates:</strong> {formatDate(selectedEvent.seriesStart)} - {formatDate(selectedEvent.seriesEnd)}</p>
        
        <h3 style={{marginTop: '30px'}}>Swims</h3>
        {loading ? <p>Loading swims...</p> : (
          <div className="swims-list">
            {swims.length === 0 ? <p>No swims found for this event.</p> : (
              <table style={{width: '100%', borderCollapse: 'collapse', marginTop: '10px'}}>
                <thead>
                  <tr style={{textAlign: 'left', borderBottom: '2px solid #eee'}}>
                    <th style={{padding: '10px'}}>Name</th>
                    <th style={{padding: '10px'}}>Start Time</th>
                    <th style={{padding: '10px'}}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {swims.map(swim => (
                    <React.Fragment key={swim.id}>
                      <tr style={{borderBottom: '1px solid #eee', cursor: 'pointer'}} onClick={() => handleSwimClick(swim)}>
                        <td style={{padding: '10px'}}>
                            {typeof swim.swimName === 'string' ? swim.swimName : (swim.swimName?.en || 'Untitled')}
                            {typeof swim.swimName === 'object' && <small style={{marginLeft: '10px', color: '#999'}}>(ML)</small>}
                        </td>
                        <td style={{padding: '10px'}}>{formatDateTime(swim.officialStart)}</td>
                        <td style={{padding: '10px'}}>
                            <button 
                                onClick={(e) => handleCheckMatches(e, swim)}
                                disabled={matchingLoading[swim.id]}
                                style={{padding: '4px 8px', fontSize: '0.8rem', cursor: 'pointer'}}
                            >
                                {matchingLoading[swim.id] ? 'Checking...' : 'Check matches'}
                            </button>
                        </td>
                      </tr>
                      {swimMatches[swim.id] && (
                        <tr>
                            <td colSpan="3" style={{padding: '10px 10px 10px 30px', backgroundColor: '#f9f9f9', fontSize: '0.9rem'}}>
                                <strong>Matches Found:</strong>
                                {swimMatches[swim.id].length === 0 ? <span style={{marginLeft: '10px'}}>None</span> : (
                                    <ul style={{margin: '5px 0', paddingLeft: '20px'}}>
                                        {swimMatches[swim.id].map(m => (
                                            <li key={m.id} style={{marginBottom: '5px'}}>
                                                <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                                                    {m.routeMatch 
                                                        ? <span style={{color: 'green', fontWeight: 'bold'}} title="Verified Route Match">✔</span> 
                                                        : <span style={{color: 'black'}} title="No Route Match">✖</span>
                                                    }
                                                    <span>ID: {m.id}</span>
                                                    {m.userId ? <span style={{color: '#555'}}>(User: {m.userId.substring(0,6)}...)</span> : ''}
                                                    {m.routeMatch && (
                                                        <button 
                                                            onClick={() => handleUpdateMatch(selectedEvent.id, swim.id, m.userId, m.id)}
                                                            disabled={updatingLoading[`${swim.id}-${m.id}`]}
                                                            style={{padding: '2px 6px', fontSize: '0.75rem', cursor: 'pointer', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '3px'}}
                                                        >
                                                            {updatingLoading[`${swim.id}-${m.id}`] ? 'Updating...' : 'Update'}
                                                        </button>
                                                    )}
                                                </div>
                                                {m.error && (
                                                    <div style={{color: '#dc3545', fontSize: '0.8rem', marginLeft: '20px'}}>
                                                        {m.error}
                                                    </div>
                                                )}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
        <button onClick={handleAddSwimClick} style={{marginTop: '20px', marginRight: '10px', padding: '10px 20px', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer'}}>
            Add New Swim
        </button>
        <button onClick={handleEditEventClick} style={{marginTop: '20px', padding: '10px 20px', backgroundColor: '#17a2b8', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer'}}>
            Edit Series Details
        </button>
        </>
        )}
      </div>
    );
  }

const EventListItem = ({ event, onSelect, formatDate }) => {

    return (
        <div
            className="official-event-tile"
            onClick={() => onSelect(event)}
        >
            {event.marketingImageUrl ? (
                <img className="official-event-tile-image" src={event.marketingImageUrl} alt={event.seriesName} />
            ) : (
                <div className="official-event-tile-image official-event-tile-image-placeholder" />
            )}
            <div className="official-event-tile-content">
                <h3>
                    {typeof event.seriesName === 'string' ? event.seriesName : (event.seriesName?.en || 'Untitled')}
                    {typeof event.seriesName === 'object' && <small>(Multilingual)</small>}
                </h3>
                <p>📍 {event.seriesLocation}</p>
                <p>📅 {formatDate(event.seriesStart)} - {formatDate(event.seriesEnd)}</p>
            </div>
        </div>
    );
};

  return (
    <div className="official-events-list">
      {loading ? <p>Loading events...</p> : (
        <>
        <button onClick={handleCreateEventClick} style={{marginBottom: '20px', padding: '10px 20px', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer'}}>+ Create New Series</button>
        <div className="events-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '20px' }}>
          {events.map(event => (
            <EventListItem
                key={event.id}
                event={event}
                onSelect={setSelectedEvent}
                formatDate={formatDate}
            />
          ))}
        </div>
        </>
      )}
    </div>
  );
};

export default OfficialEventsPage;